import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  DEFAULT_FLOOR_COLOR,
  DEFAULT_WALL_COLOR,
  FLOOR_HEIGHT_PX,
  FRAME_STYLES,
  WALL_DIMENSIONS_CM,
  WALL_HEIGHT_PX,
} from '@/pages/artwork/limited-edition-room-preview/constants';
// import { ArmchairIllustration } from '@/pages/artwork/limited-edition-room-preview/illustrations';
import { clamp } from '@/pages/artwork/limited-edition-room-preview/math';
import MuseumLabelModal from '@/pages/artwork/limited-edition-room-preview/modals/MuseumLabelModal';
import {
  buildMuseumLabelData,
  getMuseumLabelThemeVars,
  normalizeMuseumLabelPreviewKey,
} from '@/pages/artwork/limited-edition-room-preview/museumLabel';
import { getArtworkLimitedEditionOptions } from '@/pages/artwork/utils/artworkView.selectors';
import ArtworkImage from '@/shared-components/cards/components/ArtworkImage';

import useFrameMeasurements, { getFrameExtraDimensions } from './useFrameMeasurements';

import './styles/LimitedEditionRoomPreview.css';

const { width: WALL_WIDTH_CM, height: WALL_HEIGHT_CM } = WALL_DIMENSIONS_CM;

export default function LimitedEditionRoomPreview({ artworkData, previewState }) {
  const editionOptions = useMemo(() => {
    // Collect the available limited edition variants once so scaling stays consistent while users interact.
    const limitedEditionOptions = getArtworkLimitedEditionOptions(artworkData);
    if (!limitedEditionOptions || Object.keys(limitedEditionOptions).length === 0) {
      return [];
    }

    return Object.entries(limitedEditionOptions)
      .filter(([, value]) => Boolean(value))
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => (
        Number(a.width ?? 0) * Number(a.height ?? 0)
        - Number(b.width ?? 0) * Number(b.height ?? 0)
      ));
  }, [artworkData]);

  const hasSelection = Boolean(previewState?.selectedSizeData);
  const selectedEntry = hasSelection
    ? {
      key: previewState.selectedSizeKey,
      ...previewState.selectedSizeData,
    }
    : null;
  const selectedFrameKey = previewState?.selectedFrameKey || null;
  const selectedMuseumLabelKey = previewState?.selectedMuseumLabelKey || null;
  const normalizedMuseumLabelKey = normalizeMuseumLabelPreviewKey(selectedMuseumLabelKey);

  const [wallColor, setWallColor] = useState(DEFAULT_WALL_COLOR);
  const [floorColor, setFloorColor] = useState(DEFAULT_FLOOR_COLOR);
  const wallColorInputRef = useRef(null);
  const floorColorInputRef = useRef(null);
  const wallRef = useRef(null);
  const artworkSlotRef = useRef(null);
  const [wallMetrics, setWallMetrics] = useState({
    width: 0,
    height: 0,
    interiorWidth: 0,
    paddingTop: 0,
    paddingBottom: 0,
    interiorHeight: 0,
  });
  const [isMuseumLabelOpen, setIsMuseumLabelOpen] = useState(false);

  const normalizedDefaultWallColor = DEFAULT_WALL_COLOR.toLowerCase();
  const normalizedDefaultFloorColor = DEFAULT_FLOOR_COLOR.toLowerCase();
  const normalizedWallColor = typeof wallColor === 'string' ? wallColor.toLowerCase() : DEFAULT_WALL_COLOR;
  const isCustomWallColor = normalizedWallColor !== normalizedDefaultWallColor;
  const normalizedFloorColor = typeof floorColor === 'string' ? floorColor.toLowerCase() : DEFAULT_FLOOR_COLOR;
  const isCustomFloorColor = normalizedFloorColor !== normalizedDefaultFloorColor;

  const handleOpenColorPicker = (inputRef) => {
    if (inputRef.current?.showPicker) {
      inputRef.current.showPicker();
      return;
    }

    inputRef.current?.click();
  };

  const handleWallColorChange = (event) => {
    setWallColor(event.target.value);
  };

  const handleResetWallColor = () => {
    setWallColor(DEFAULT_WALL_COLOR);
  };

  const handleFloorColorChange = (event) => {
    setFloorColor(event.target.value);
  };

  const handleResetFloorColor = () => {
    setFloorColor(DEFAULT_FLOOR_COLOR);
  };

  const widthCm = selectedEntry?.width ? Number(selectedEntry.width) : 0;
  const heightCm = selectedEntry?.height ? Number(selectedEntry.height) : 0;
  const aspectRatio = widthCm > 0 ? (heightCm / widthCm) : 1; // >1 = taller than wide
  const mainImageId = artworkData?.artwork_media?.artwork_main_picture?.image_id;
  const selectedFrame = useMemo(() => {
    if (!selectedFrameKey) {
      return null;
    }

    return FRAME_STYLES[selectedFrameKey] || null;
  }, [selectedFrameKey]);
  const selectedFrameClassName = selectedFrame?.className || null;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const wallNode = wallRef.current;
    if (!wallNode) {
      return undefined;
    }

    const computeMetrics = () => {
      if (!wallRef.current) {
        return;
      }

      // Treat the measured wall span (minus padding) as the physical two-metre surface.
      const { width, height } = wallRef.current.getBoundingClientRect();
      const styles = window.getComputedStyle(wallRef.current);
      const paddingLeft = parseFloat(styles.paddingLeft) || 0;
      const paddingRight = parseFloat(styles.paddingRight) || 0;
      const paddingTop = parseFloat(styles.paddingTop) || 0;
      const paddingBottom = parseFloat(styles.paddingBottom) || 0;

      const interiorWidth = Math.max(width - paddingLeft - paddingRight, 0);
      const interiorHeight = Math.max(height - paddingTop - paddingBottom, 0);

      setWallMetrics({
        width,
        height,
        interiorWidth,
        paddingTop,
        paddingBottom,
        interiorHeight,
      });
    };

    computeMetrics();

    let resizeObserver;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        computeMetrics();
      });

      resizeObserver.observe(wallNode);

    } else {
      window.addEventListener('resize', computeMetrics);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', computeMetrics);
      }
    };
  }, []);

  const pxPerCmFallback = WALL_HEIGHT_PX / WALL_HEIGHT_CM;
  // Map artwork centimetres to pixels using the available two-metre span whenever possible.
  const pxPerCmFromWidth = wallMetrics.interiorWidth > 0
    ? wallMetrics.interiorWidth / WALL_WIDTH_CM
    : null;
  const pxPerCm = pxPerCmFromWidth ?? pxPerCmFallback;
  const {
    extraWidthPx: frameExtraWidthPx,
    extraHeightPx: frameExtraHeightPx,
  } = getFrameExtraDimensions(selectedFrame, pxPerCm);

  let artworkWidthPx = widthCm > 0 ? widthCm * pxPerCm : 0;
  let artworkHeightPx = heightCm > 0 ? heightCm * pxPerCm : 0;

  const wallContentHeight = Math.max(wallMetrics.interiorHeight, 0);
  const floorReserveRatio = clamp((heightCm - 40) / 60, 0, 0.35);
  const rawFloorReserve = wallContentHeight > 0
    ? Math.max(Math.round(wallContentHeight * floorReserveRatio), 32)
    : 0;
  const reservedFloorHeightPx = wallContentHeight > 0
    ? Math.min(Math.max(rawFloorReserve, FLOOR_HEIGHT_PX), wallContentHeight)
    : 0;
  const wallHeightWithoutPadding = Math.max(wallContentHeight - reservedFloorHeightPx, 0);
  const availableArtworkHeight = Math.max(
    wallHeightWithoutPadding - frameExtraHeightPx,
    0,
  );

  if (availableArtworkHeight > 0 && artworkHeightPx > availableArtworkHeight) {
    const scale = availableArtworkHeight / (artworkHeightPx || 1);
    artworkWidthPx *= scale;
    artworkHeightPx *= scale;
  }

  const availableArtworkWidth = Math.max(wallMetrics.interiorWidth - frameExtraWidthPx, 0);

  if (availableArtworkWidth > 0 && artworkWidthPx > availableArtworkWidth) {
    const scale = availableArtworkWidth / (artworkWidthPx || 1);
    artworkWidthPx *= scale;
    artworkHeightPx *= scale;
  }

  // Extra cap for very tall, narrow artworks so they don’t dominate the wall
  const TALL_ASPECT_THRESHOLD = 2.2;     // only trigger when height >> width
  const TALL_MAX_HEIGHT_RATIO = 0.6;     // at most 60% of usable wall height

  if (aspectRatio > TALL_ASPECT_THRESHOLD && wallHeightWithoutPadding > 0) {
    const tallMaxHeightPx = wallHeightWithoutPadding * TALL_MAX_HEIGHT_RATIO;
    if (artworkHeightPx > tallMaxHeightPx) {
      const scale = tallMaxHeightPx / (artworkHeightPx || 1);
      artworkWidthPx *= scale;
      artworkHeightPx *= scale;
    }
  }

  const effectivePxPerCm = widthCm > 0 ? (artworkWidthPx / widthCm) : pxPerCm;
  const safePxPerCm = Number.isFinite(effectivePxPerCm) && effectivePxPerCm > 0 ? effectivePxPerCm : 0;

  const {
    renderedWidthPx: framedArtworkWidthPx,
    frameInlineStyles,
    matInlineStyles,
  } = useFrameMeasurements({
    frame: selectedFrame,
    pxPerCm: safePxPerCm,
    artworkWidthPx,
    artworkHeightPx,
  });

  const artworkSlotStyle = hasSelection
    ? {
      // constrain width; height is dictated by the scaled artwork/frame
      width: framedArtworkWidthPx > 0 ? `${framedArtworkWidthPx}px` : undefined,
    }
    : undefined;

  const labelWidthPx = safePxPerCm > 0
    ? Math.round(clamp(safePxPerCm * 6, 55, 100))
    : 120;
  const labelHeightPx = safePxPerCm > 0
    ? Math.round(clamp(safePxPerCm * 2.15, 32, 60))
    : 60;
  const labelGapPx = safePxPerCm > 0
    ? Math.max(Math.round(safePxPerCm * 0.5), 4)
    : 12;

  const museumLabelData = useMemo(
    () => buildMuseumLabelData({
      artworkData,
      selectedEntry,
      widthCm,
      heightCm,
      normalizedMuseumLabelKey,
    }),
    [artworkData, heightCm, normalizedMuseumLabelKey, selectedEntry, widthCm],
  );

  const fallbackMessage = editionOptions.length > 0
    ? 'Select a size to preview the edition in your space.'
    : 'Limited edition dimensions will be available soon.';
  const canRenderArtwork = hasSelection && artworkWidthPx > 0 && artworkHeightPx > 0 && mainImageId;
  const canOpenLabelModal = Boolean(hasSelection && museumLabelData);

  const hasLabelSelection = Boolean(museumLabelData);

  useEffect(() => {
    if (!canOpenLabelModal) {
      setIsMuseumLabelOpen(false);
    }
  }, [canOpenLabelModal]);

  const floorStyle = {
    backgroundColor: floorColor,
  };

  const museumLabelThemeVars = museumLabelData?.theme ? getMuseumLabelThemeVars(museumLabelData.theme) : null;
  const availableHorizontalSpace = Math.max(
    (wallMetrics.interiorWidth - framedArtworkWidthPx) / 2,
    0,
  );
  const shouldFlipLabel = (labelWidthPx + labelGapPx) > availableHorizontalSpace && availableHorizontalSpace > 0;
  const museumLabelInlineStyle = {
    width: `${labelWidthPx}px`,
    height: `${labelHeightPx}px`,
    ...(museumLabelThemeVars || {}),
  };
  const museumLabelWrapperStyle = {
    '--museum-label-gap': `${labelGapPx}px`,
  };
  const museumLabelWrapperClassName = `limited-edition-room-preview__museum-label-wrapper${shouldFlipLabel ? ' is-flipped' : ''}`;
  const canDisplayLabel = Boolean(hasSelection && hasLabelSelection);
  const wallClassName = `limited-edition-room-preview__wall${canOpenLabelModal ? ' is-clickable' : ''}`;
  const wallRole = canOpenLabelModal ? 'button' : 'presentation';
  const wallTabIndex = canOpenLabelModal ? 0 : undefined;

  const handleWallClick = () => {
    if (!canOpenLabelModal) {
      return;
    }

    setIsMuseumLabelOpen(true);
  };

  const handleWallKeyDown = (event) => {
    if (!canOpenLabelModal) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsMuseumLabelOpen(true);
    }
  };

  const handleCloseMuseumLabel = () => {
    setIsMuseumLabelOpen(false);
  };

  return (
    <div className="limited-edition-room-preview">
      <div className="limited-edition-room-preview__room" role="presentation">
        <div
          ref={wallRef}
          className={wallClassName}
          style={{ backgroundColor: wallColor }}
          role={wallRole}
          tabIndex={wallTabIndex}
          onClick={canOpenLabelModal ? handleWallClick : undefined}
          onKeyDown={canOpenLabelModal ? handleWallKeyDown : undefined}
          aria-label={canOpenLabelModal ? 'View museum label details' : undefined}
        >
          {/* <div className="limited-edition-room-preview__illustration limited-edition-room-preview__illustration--left">
            <ArmchairIllustration className="limited-edition-room-preview__illustration-svg" />
          </div> */}

          <div
            className={`limited-edition-room-preview__artwork-slot${hasSelection ? '' : ' is-empty'}`}
            style={artworkSlotStyle}
            ref={artworkSlotRef}
          >
            {canRenderArtwork ? (
              selectedFrame ? (
                <div
                  className={selectedFrameClassName
                    ? `limited-edition-room-preview__frame ${selectedFrameClassName}`
                    : 'limited-edition-room-preview__frame'}
                  style={frameInlineStyles}
                >
                  <div
                    className="limited-edition-room-preview__frame-mat"
                    style={matInlineStyles}
                  >
                    <ArtworkImage
                      artworkData={artworkData}
                      image={mainImageId}
                      imageClassName="limited-edition-room-preview__artwork-image"
                      imageTemplateClassName="limited-edition-room-preview__artwork-template"
                      enabledDoubleClick={false}
                      disableDownload
                    />
                  </div>
                </div>
              ) : (
                <ArtworkImage
                  artworkData={artworkData}
                  image={mainImageId}
                  imageClassName="limited-edition-room-preview__artwork-image"
                  imageTemplateClassName="limited-edition-room-preview__artwork-template"
                  enabledDoubleClick={false}
                  disableDownload
                />
              )
            ) : (
              <span className="limited-edition-room-preview__placeholder">
                {fallbackMessage}
              </span>
            )}
            {canDisplayLabel ? (
              <div
                className={museumLabelWrapperClassName}
                style={museumLabelWrapperStyle}
              >
                <div
                  className="limited-edition-room-preview__museum-label"
                  style={museumLabelInlineStyle}
                  aria-hidden="true"
                />
              </div>
            ) : null}
          </div>

          {/* <div className="limited-edition-room-preview__illustration limited-edition-room-preview__illustration--right">
            <SideTableLampIllustration className="limited-edition-room-preview__illustration-svg" />
          </div> */}
        </div>

        <div className="limited-edition-room-preview__floor" style={floorStyle} />
      </div>

      <div className="limited-edition-room-preview__controls">
        <div className="limited-edition-room-preview__wall-color-picker">
          <button
            type="button"
            className="limited-edition-room-preview__control-button limited-edition-room-preview__wall-color-button"
            onClick={() => handleOpenColorPicker(wallColorInputRef)}
            aria-describedby="limited-edition-wall-color-value"
          >
            Pick Wall Color
          </button>

          <input
            ref={wallColorInputRef}
            type="color"
            className="limited-edition-room-preview__wall-color-input"
            value={wallColor}
            onChange={handleWallColorChange}
            aria-label="Choose wall color"
          />

          <div className="limited-edition-room-preview__wall-color-feedback" role="group" aria-label="Wall color">
            <span
              id="limited-edition-wall-color-value"
              className="limited-edition-room-preview__wall-color-dot"
              style={{ backgroundColor: wallColor }}
              aria-label={`Current wall color ${wallColor}`}
              title={wallColor}
            />
            {isCustomWallColor ? (
              <button
                type="button"
                className="limited-edition-room-preview__wall-color-reset"
                onClick={handleResetWallColor}
                aria-label="Reset wall color to default"
                title="Reset wall color"
              >
                Reset
              </button>
            ) : null}
          </div>
        </div>

        <div className="limited-edition-room-preview__wall-color-picker">
          <button
            type="button"
            className="limited-edition-room-preview__control-button limited-edition-room-preview__wall-color-button"
            onClick={() => handleOpenColorPicker(floorColorInputRef)}
            aria-describedby="limited-edition-floor-color-value"
          >
            Pick Floor Color
          </button>

          <input
            ref={floorColorInputRef}
            type="color"
            className="limited-edition-room-preview__wall-color-input"
            value={floorColor}
            onChange={handleFloorColorChange}
            aria-label="Choose floor color"
          />

          <div className="limited-edition-room-preview__wall-color-feedback" role="group" aria-label="Floor color">
            <span
              id="limited-edition-floor-color-value"
              className="limited-edition-room-preview__wall-color-dot"
              style={{ backgroundColor: floorColor }}
              aria-label={`Current floor color ${floorColor}`}
              title={floorColor}
            />
            {isCustomFloorColor ? (
              <button
                type="button"
                className="limited-edition-room-preview__wall-color-reset"
                onClick={handleResetFloorColor}
                aria-label="Reset floor color to default"
                title="Reset floor color"
              >
                Reset
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {canOpenLabelModal ? (
        <MuseumLabelModal
          isOpen={isMuseumLabelOpen}
          onClose={handleCloseMuseumLabel}
          labelData={museumLabelData}
        />
      ) : null}
    </div>
  );
}
